import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Cookie, Shield, Settings, BarChart3, Users, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

const CookiePolicyPage = () => {
    return (
        <div className="min-h-screen bg-background py-8">
            <div className="container mx-auto px-4 max-w-4xl">
                {/* Back Button */}
                <Link to="/">
                    <Button variant="ghost" className="mb-6">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Înapoi la pagina principală
                    </Button>
                </Link>

                {/* Header */}
                <Card className="rounded-2xl mb-6">
                    <CardHeader className="text-center pb-4">
                        <div className="flex justify-center mb-4">
                            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                                <Cookie className="w-8 h-8 text-primary" />
                            </div>
                        </div>
                        <CardTitle className="text-3xl font-heading">Politica de Cookies</CardTitle>
                        <p className="text-muted-foreground mt-2">
                            Ultima actualizare: {new Date().toLocaleDateString('ro-RO', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                    </CardHeader>
                </Card>

                {/* Introduction */}
                <Card className="rounded-2xl mb-6">
                    <CardContent className="p-6">
                        <h2 className="text-xl font-semibold mb-4">1. Introducere</h2>
                        <p className="text-muted-foreground mb-4">
                            Această Politică de Cookies explică ce sunt cookie-urile, cum le folosim pe platforma Colectarium, 
                            ce tipuri de cookie-uri utilizăm și cum puteți gestiona preferințele dumneavoastră privind cookie-urile.
                        </p>
                        <p className="text-muted-foreground">
                            Prin utilizarea site-ului nostru, sunteți de acord cu utilizarea cookie-urilor în conformitate 
                            cu această politică. Dacă nu sunteți de acord cu utilizarea cookie-urilor, vă rugăm să ajustați 
                            setările browserului sau să nu utilizați site-ul nostru.
                        </p>
                    </CardContent>
                </Card>

                {/* What are cookies */}
                <Card className="rounded-2xl mb-6">
                    <CardContent className="p-6">
                        <h2 className="text-xl font-semibold mb-4">2. Ce sunt cookie-urile?</h2>
                        <p className="text-muted-foreground mb-4">
                            Cookie-urile sunt fișiere text de mici dimensiuni care sunt plasate pe dispozitivul dumneavoastră 
                            (computer, tabletă sau telefon mobil) atunci când vizitați un site web. Cookie-urile sunt utilizate 
                            pe scară largă pentru a face site-urile web să funcționeze mai eficient, pentru a îmbunătăți 
                            experiența utilizatorului și pentru a furniza informații proprietarilor site-ului.
                        </p>
                        <p className="text-muted-foreground">
                            Cookie-urile nu pot accesa alte informații de pe dispozitivul dumneavoastră și nu pot transmite 
                            viruși sau alte programe malițioase.
                        </p>
                    </CardContent>
                </Card>

                {/* Types of cookies */}
                <Card className="rounded-2xl mb-6">
                    <CardContent className="p-6">
                        <h2 className="text-xl font-semibold mb-6">3. Tipurile de cookie-uri pe care le utilizăm</h2>
                        
                        {/* Essential Cookies */}
                        <div className="mb-6 p-4 bg-green-50 rounded-xl border border-green-200">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                                    <Shield className="w-5 h-5 text-green-600" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-green-800">Cookie-uri esențiale (obligatorii)</h3>
                                    <p className="text-sm text-green-600">Necesare pentru funcționarea site-ului</p>
                                </div>
                            </div>
                            <p className="text-sm text-muted-foreground mb-3">
                                Aceste cookie-uri sunt strict necesare pentru funcționarea site-ului nostru. Ele vă permit 
                                să navigați pe site și să utilizați funcțiile sale, cum ar fi accesarea zonelor securizate.
                            </p>
                            <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                                <li><strong>token</strong> - Token de autentificare pentru sesiunea utilizatorului</li>
                                <li><strong>user</strong> - Informații despre utilizatorul autentificat</li>
                                <li><strong>cookie_consent</strong> - Salvează preferințele privind cookie-urile</li>
                            </ul>
                        </div>

                        {/* Functional Cookies */}
                        <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                                    <Settings className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-blue-800">Cookie-uri funcționale</h3>
                                    <p className="text-sm text-blue-600">Îmbunătățesc experiența utilizatorului</p>
                                </div>
                            </div>
                            <p className="text-sm text-muted-foreground mb-3">
                                Aceste cookie-uri permit site-ului să rețină alegerile pe care le faceți și să ofere 
                                funcționalități îmbunătățite și mai personalizate.
                            </p>
                            <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                                <li><strong>recent_searches</strong> - Salvează căutările recente pentru acces rapid</li>
                                <li><strong>theme</strong> - Preferințe de afișare (mod întunecat/luminos)</li>
                                <li><strong>notification_preferences</strong> - Setări pentru notificări</li>
                            </ul>
                        </div>

                        {/* Analytics Cookies */}
                        <div className="mb-6 p-4 bg-purple-50 rounded-xl border border-purple-200">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                                    <BarChart3 className="w-5 h-5 text-purple-600" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-purple-800">Cookie-uri analitice</h3>
                                    <p className="text-sm text-purple-600">Ne ajută să înțelegem utilizarea site-ului</p>
                                </div>
                            </div>
                            <p className="text-sm text-muted-foreground mb-3">
                                Aceste cookie-uri ne permit să numărăm vizitele și sursele de trafic, astfel încât să 
                                putem măsura și îmbunătăți performanța site-ului nostru.
                            </p>
                            <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                                <li>Numărul de vizitatori și paginile vizitate</li>
                                <li>Durata sesiunilor și rata de respingere</li>
                                <li>Dispozitivele și browserele utilizate</li>
                            </ul>
                        </div>

                        {/* Third-party Cookies */}
                        <div className="p-4 bg-orange-50 rounded-xl border border-orange-200">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                                    <Users className="w-5 h-5 text-orange-600" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-orange-800">Cookie-uri terțe părți</h3>
                                    <p className="text-sm text-orange-600">Furnizate de servicii externe</p>
                                </div>
                            </div>
                            <p className="text-sm text-muted-foreground mb-3">
                                Utilizăm servicii terțe care pot plasa cookie-uri pe dispozitivul dumneavoastră:
                            </p>
                            <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                                <li><strong>Google OAuth</strong> - Pentru autentificarea cu contul Google</li>
                                <li><strong>Stripe</strong> - Pentru procesarea plăților securizate</li>
                            </ul>
                        </div>
                    </CardContent>
                </Card>

                {/* Cookie Duration */}
                <Card className="rounded-2xl mb-6">
                    <CardContent className="p-6">
                        <h2 className="text-xl font-semibold mb-4">4. Durata de viață a cookie-urilor</h2>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b">
                                        <th className="text-left py-3 px-4 font-semibold">Tip cookie</th>
                                        <th className="text-left py-3 px-4 font-semibold">Durată</th>
                                    </tr>
                                </thead>
                                <tbody className="text-muted-foreground">
                                    <tr className="border-b">
                                        <td className="py-3 px-4">Cookie-uri de sesiune</td>
                                        <td className="py-3 px-4">Se șterg când închideți browserul</td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="py-3 px-4">Token de autentificare</td>
                                        <td className="py-3 px-4">24 ore sau până la deconectare</td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="py-3 px-4">Preferințe utilizator</td>
                                        <td className="py-3 px-4">1 an</td>
                                    </tr>
                                    <tr>
                                        <td className="py-3 px-4">Consimțământ cookie-uri</td>
                                        <td className="py-3 px-4">1 an</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>

                {/* Managing Cookies */}
                <Card className="rounded-2xl mb-6">
                    <CardContent className="p-6">
                        <h2 className="text-xl font-semibold mb-4">5. Cum puteți gestiona cookie-urile</h2>
                        <p className="text-muted-foreground mb-4">
                            Majoritatea browserelor web vă permit să controlați cookie-urile prin setările lor. 
                            Puteți configura browserul să:
                        </p>
                        <ul className="text-muted-foreground space-y-2 ml-4 list-disc mb-4">
                            <li>Accepte toate cookie-urile</li>
                            <li>Vă notifice când este plasat un cookie</li>
                            <li>Refuze toate cookie-urile</li>
                            <li>Ștergeți cookie-urile existente</li>
                        </ul>
                        <p className="text-muted-foreground mb-4">
                            <strong>Atenție:</strong> Dacă dezactivați cookie-urile esențiale, este posibil ca anumite 
                            funcționalități ale site-ului să nu funcționeze corect.
                        </p>
                        
                        <h3 className="font-semibold mb-3 mt-6">Instrucțiuni pentru browsere populare:</h3>
                        <div className="grid gap-3">
                            <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" 
                               className="text-primary hover:underline">→ Google Chrome</a>
                            <a href="https://support.mozilla.org/ro/kb/cookies-informatii-site-uri-stocate-calculator" target="_blank" rel="noopener noreferrer"
                               className="text-primary hover:underline">→ Mozilla Firefox</a>
                            <a href="https://support.apple.com/ro-ro/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer"
                               className="text-primary hover:underline">→ Safari</a>
                            <a href="https://support.microsoft.com/ro-ro/microsoft-edge/ștergeți-cookie-urile-în-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer"
                               className="text-primary hover:underline">→ Microsoft Edge</a>
                        </div>
                    </CardContent>
                </Card>

                {/* Updates */}
                <Card className="rounded-2xl mb-6">
                    <CardContent className="p-6">
                        <h2 className="text-xl font-semibold mb-4">6. Actualizări ale politicii</h2>
                        <p className="text-muted-foreground">
                            Ne rezervăm dreptul de a actualiza această Politică de Cookies periodic. Orice modificări 
                            vor fi publicate pe această pagină cu o nouă dată de „Ultima actualizare". Vă recomandăm 
                            să verificați această pagină regulat pentru a fi la curent cu eventualele modificări.
                        </p>
                    </CardContent>
                </Card>

                {/* Contact */}
                <Card className="rounded-2xl">
                    <CardContent className="p-6">
                        <h2 className="text-xl font-semibold mb-4">7. Contact</h2>
                        <p className="text-muted-foreground mb-4">
                            Dacă aveți întrebări despre această Politică de Cookies sau despre modul în care utilizăm 
                            cookie-urile, ne puteți contacta la:
                        </p>
                        <div className="bg-gray-50 rounded-xl p-4">
                            <p className="font-semibold">Colectarium</p>
                            <p className="text-muted-foreground">Email: contact@colectarium.ro</p>
                        </div>
                    </CardContent>
                </Card>

                {/* Footer Links */}
                <div className="mt-8 text-center text-sm text-muted-foreground">
                    <p>
                        Vezi și: <Link to="/terms" className="text-primary hover:underline">Termeni și Condiții</Link>
                        {' • '}
                        <Link to="/privacy" className="text-primary hover:underline">Politica de Confidențialitate</Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default CookiePolicyPage;
